import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, vShow as _vShow, normalizeClass as _normalizeClass, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = {
  key: 0,
  class: "absolute inset-0 flex flex-col items-center justify-center bg-gray-50 rounded-lg"
}
const _hoisted_3 = {
  key: 1,
  class: "absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-gray-50 rounded-lg"
}

import { ref, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import http from "../services/http";
import { useAppOrchestratorStore } from "../store/appOrchestrator";

/* eslint-disable-next-line no-undef -- Vue 3 script setup compiler macro */
type AppRecord = {
  id: number;
  label: string;
  route: string;
  iframe_url: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'EmbeddedAppPool',
  props: {
  fullscreen: { type: Boolean, default: false },
},
  setup(__props) {



const EMBEDDED_ROUTE_NAMES = [
  "CustomAppBySectionAndSlug",
  "CustomAppBySlug",
  "CustomAppDirectSlug",
  "CustomAppById",
];

const route = useRoute();
const orchestrator = useAppOrchestratorStore();
const loading = ref(false);
/** Key we're currently loading; used to show "No Application Found" when load fails. */
const currentRequestedKey = ref<string | null>(null);

function isLoopbackHost(value: string): boolean {
  const normalized = value.trim().toLowerCase();

  return (
    normalized === "localhost" ||
    normalized === "127.0.0.1" ||
    normalized === "::1"
  );
}

function resolveLocalIframeOrigin(): string {
  const configured = String(
    process.env.VUE_APP_LOCAL_IFRAME_ORIGIN || ""
  ).trim();

  if (configured) {
    return configured;
  }

  return "http://localhost:5173";
}

function rewriteIframeUrlForLocalDev(url: string): string {
  if (!url) {
    return "";
  }

  if (process.env.NODE_ENV !== "development") {
    return url;
  }

  try {
    const resolvedUrl = new URL(url);
    const currentHost = window.location.hostname.trim().toLowerCase();

    if (isLoopbackHost(currentHost) && !isLoopbackHost(resolvedUrl.hostname)) {
      const localOrigin = resolveLocalIframeOrigin();
      const localUrl = new URL(localOrigin);
      resolvedUrl.protocol = localUrl.protocol;
      resolvedUrl.host = localUrl.host;
    }

    return resolvedUrl.toString();
  } catch {
    return url;
  }
}

/**
 * Tell embedded microapps exactly which window owns the SSO session. Relying
 * only on document.referrer/ancestorOrigins is not reliable with restrictive
 * referrer policies, and causes the iframe to never receive SSO_GRANT.
 */
function appendParentOrigin(url: string): string {
  try {
    const iframeUrl = new URL(url, window.location.origin);
    iframeUrl.searchParams.set("parent_origin", window.location.origin);
    return iframeUrl.toString();
  } catch {
    return url;
  }
}

function isEmbeddedRoute(): boolean {
  return !!route.name && EMBEDDED_ROUTE_NAMES.includes(route.name as string);
}

function getPoolKey(): string | null {
  const params = route.params as { slug?: string; id?: string };
  if (params.slug) return params.slug;
  if (params.id) return `id:${params.id}`;
  return null;
}

async function loadAndRegister() {
  const key = getPoolKey();
  if (!key) {
    currentRequestedKey.value = null;
    return;
  }

  loading.value = true;
  currentRequestedKey.value = key;
  try {
    const params = route.params as { slug?: string; id?: string };
    let app: AppRecord | null = null;

    if (params.slug) {
      const { data } = await http.get<AppRecord>(
        `/api/sidenav-items/slug/${params.slug}`
      );
      app = data;
    } else if (params.id) {
      const { data } = await http.get<AppRecord>(
        `/api/sidenav-items/${params.id}`
      );
      app = data;
    }

    if (app?.iframe_url) {
      orchestrator.ensureEmbedded(
        key,
        appendParentOrigin(
          rewriteIframeUrlForLocalDev(app.iframe_url)
        ),
        app.label || ""
      );
      currentRequestedKey.value = null;
    } else {
      console.warn("No iframe_url found for this app.");
      orchestrator.evictByKey(key);
      orchestrator.setActiveEmbeddedKey(null);
    }
  } catch (err) {
    console.error("Failed to load app", err);
    orchestrator.evictByKey(key);
    orchestrator.setActiveEmbeddedKey(null);
  } finally {
    loading.value = false;
  }
}

function maybeLoadAndRegister() {
  if (isEmbeddedRoute()) {
    loadAndRegister();
  } else {
    currentRequestedKey.value = null;
    orchestrator.clearActiveEmbedded();
  }
}

onMounted(maybeLoadAndRegister);
watch(
  () => [route.name, route.params.slug, route.params.id],
  maybeLoadAndRegister
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["embedded-pool h-[100vh] w-full overflow-hidden p-4", { 'embedded-pool--fullscreen': __props.fullscreen }])
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(orchestrator).cachedEmbeddedApps, (entry) => {
      return _withDirectives((_openBlock(), _createElementBlock("div", {
        key: entry.key,
        class: _normalizeClass(["embedded-iframe-wrapper absolute overflow-hidden", [
        { 'z-10': _unref(orchestrator).activeEmbeddedKey === entry.key },
        __props.fullscreen ? 'inset-0' : 'inset-4',
      ]])
      }, [
        _createElementVNode("iframe", {
          src: entry.url,
          class: "embedded-iframe w-full border-0"
        }, null, 8, _hoisted_1)
      ], 2)), [
        [_vShow, _unref(orchestrator).activeEmbeddedKey === entry.key]
      ])
    }), 128)),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
          _createElementVNode("div", { class: "w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" }, null, -1),
          _createElementVNode("p", { class: "text-gray-500 text-sm" }, " Loading application... ", -1)
        ]))]))
      : (
        currentRequestedKey.value && !loading.value && !_unref(orchestrator).activeEmbedded
      )
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[1] || (_cache[1] = [
            _createElementVNode("div", { class: "text-4xl mb-4" }, " ⚠️ ", -1),
            _createElementVNode("h2", { class: "text-xl font-semibold text-gray-800" }, " No Application Found ", -1),
            _createElementVNode("p", { class: "text-gray-500 mt-2 max-w-md" }, " This application does not have a valid URL. Please contact the administrator or check the side navigation configuration. ", -1)
          ]))]))
        : _createCommentVNode("", true)
  ], 2))
}
}

})